<template>
    <div class="withdrawal-request-view">
        <!-- Статус заявки -->
        <b-card class="mb-4">
            <div class="d-flex justify-content-between align-items-center">
                <div>
                    <h5 class="mb-1">Статус заявки (ID: {{ withdrawalRequest.id }})</h5>
                    <b-badge v-if="withdrawalRequest && withdrawalRequest.status" :variant="getWithdrawalStatusVariant(withdrawalRequest.status)" class="p-2">
                        {{ getWithdrawalStatusLabel(withdrawalRequest.status) }}
                    </b-badge>
                </div>
                <div class="text-right" v-if="withdrawalRequest && withdrawalRequest.amount">
                    <div class="h4 text-success mb-0 font-weight-bold">{{ formatCurrency(withdrawalRequest.amount) }}</div>
                    <small class="text-muted">Сумма вывода</small>
                </div>
            </div>
        </b-card>

        <!-- Информация о заявке -->
        <b-card class="mb-4">
            <template #header>
                <h6 class="mb-0">Информация о заявке</h6>
            </template>
            <b-row v-if="withdrawalRequest">
                <b-col cols="12" md="6">
                    <p v-if="withdrawalRequest.created_at"><strong>Дата создания:</strong> {{ formatDate(withdrawalRequest.created_at) }}</p>
                    <p v-if="withdrawalRequest.act_generated_at">
                        <strong>Акт сформирован:</strong> {{ formatDate(withdrawalRequest.act_generated_at) }}
                    </p>
                    <p v-if="withdrawalRequest.act_sent_at">
                        <strong>Акт отправлен:</strong> {{ formatDate(withdrawalRequest.act_sent_at) }}
                    </p>
                </b-col>
                <b-col cols="12" md="6">
                    <p v-if="withdrawalRequest.act_received_at">
                        <strong>Акт получен:</strong> {{ formatDate(withdrawalRequest.act_received_at) }}
                    </p>
                    <p v-if="withdrawalRequest.funds_sent_at">
                        <strong>Средства отправлены:</strong> {{ formatDate(withdrawalRequest.funds_sent_at) }}
                    </p>
                    <p v-if="remainingBalance > 0">
                        <strong>Остаток на балансе:</strong> 
                        <span class="text-success font-weight-bold">{{ formatCurrency(remainingBalance) }}</span>
                    </p>
                </b-col>
            </b-row>
        </b-card>


        <!-- Статус обработки -->
        <b-card v-if="withdrawalRequest && (withdrawalRequest.status === 'act_sent' || withdrawalRequest.status === 'act_received')" class="mb-4">
            <template #header>
                <h6 class="mb-0">Статус обработки</h6>
            </template>
            <div v-if="withdrawalRequest.status === 'act_sent'">
                <b-alert variant="info" show>
                    <i class="fas fa-info-circle mr-2"></i>
                    Акт отправлен. Ожидаем получения оригинала акта. После получения средства будут отправлены в течение 1-3 рабочих дней.
                </b-alert>
            </div>
            <div v-if="withdrawalRequest.status === 'act_received'">
                <b-alert variant="success" show>
                    <i class="fas fa-check-circle mr-2"></i>
                    Акт получен. Средства будут отправлены в течение 1-3 рабочих дней.
                </b-alert>
            </div>
        </b-card>

        <!-- Средства отправлены -->
        <b-card v-if="withdrawalRequest && withdrawalRequest.status === 'funds_sent'" class="mb-4">
            <template #header>
                <h6 class="mb-0 text-success">Средства отправлены</h6>
            </template>
            <b-alert variant="success" show>
                <i class="fas fa-check-circle mr-2"></i>
                <strong>Средства успешно отправлены!</strong> 
                <span v-if="withdrawalRequest.funds_sent_at">Дата отправки: {{ formatDate(withdrawalRequest.funds_sent_at) }}</span>
            </b-alert>
        </b-card>

        <!-- Операции в заявке -->
        <b-card class="mb-4" v-if="settlements && settlements.length > 0">
            <template #header>
                <h6 class="mb-0">Операции, включенные в заявку</h6>
            </template>
            <b-table
                :items="settlements || []"
                :fields="settlementFields"
                small
                striped
                hover
                responsive
                :per-page="10"
            >
                <template #cell(amount)="row">
                    <strong class="text-success">{{ formatCurrency(row.item.amount) }}</strong>
                </template>
                <template #cell(status)="row">
                    <b-badge :variant="getStatusVariant(row.item.status)">
                        {{ getStatusLabel(row.item.status) }}
                    </b-badge>
                </template>
            </b-table>
        </b-card>
        <b-alert v-else variant="light" show class="mb-4">
            Операции, включенные в заявку, отсутствуют.
        </b-alert>

        <!-- Инструкции для агента -->
        <b-card class="mb-4" v-if="withdrawalRequest && (withdrawalRequest.status === 'pending' || withdrawalRequest.status === 'act_generated')">
            <template #header>
                <h6 class="mb-0 text-primary">
                    <i class="fas fa-info-circle mr-2"></i>
                    Процедура получения вознаграждения
                </h6>
            </template>
            
            <div class="mb-3">
                <ol>
                    <li class="mb-2">
                        <strong>Подписать акт</strong> - после формирования акта необходимо его подписать
                    </li>
                    <li class="mb-2">
                        <strong>Отправить оригинал к нам по почте</strong> - отправьте подписанный оригинал акта по адресу компании: 121552, Москва, ОПС 121552, а/я 99
                    </li>
                    <li class="mb-2">
                        <strong>Ожидание обработки</strong> - после получения акта средства будут отправлены в течение 1-3 рабочих дней
                    </li>
                </ol>
            </div>

            <div class="d-flex gap-2 flex-wrap">
                <b-button
                    v-if="withdrawalRequest.status === 'act_generated'"
                    variant="success"
                    @click="handleMarkActSent"
                    :disabled="loadingAct"
                    size="sm"
                >
                    <b-spinner small v-if="loadingAct" class="mr-1"></b-spinner>
                    <i class="fas fa-paper-plane mr-1" v-else></i>
                    Акт отправлен по почте
                </b-button>
            </div>
        </b-card>

        <!-- Акт -->
        <b-card class="mb-4" v-if="withdrawalRequest">
            <template #header>
                <h6 class="mb-0">Акт выполненных работ</h6>
            </template>
            
            <div class="act-document" style="font-family: Arial, sans-serif; padding: 20px;">
                <!-- Заголовок -->
                <div class="text-center mb-4">
                    <h5 class="mb-2"><strong>{{ agentCompanyName }}</strong></h5>
                    <p class="mb-0">Отчетный период с {{ formatActDate(periodStart) }} по {{ formatActDate(periodEnd) }}</p>
                </div>

                <!-- Раздел 1: Клиенты и договоры -->
                <div class="mb-4">
                    <p class="mb-2">
                        1. Агент нашел <strong>{{ uniqueClientsCount }}</strong> {{ uniqueClientsCount === 1 ? 'клиента' : uniqueClientsCount < 5 ? 'клиента' : 'клиентов' }}, 
                        с {{ uniqueClientsCount === 1 ? 'которым' : 'которыми' }} были проведены переговоры, 
                        и {{ uniqueClientsCount === 1 ? 'заключен договор' : 'заключены договоры' }} с ООО «ТАНАИС БРОКЕР».
                    </p>
                    <b-table 
                        :items="clientsTable" 
                        :fields="clientsTableFields"
                        small
                        bordered
                        class="mb-0"
                    >
                    </b-table>
                </div>

                <!-- Раздел 2: Расчет вознаграждения -->
                <div class="mb-4">
                    <p class="mb-2">2. Агентское вознаграждение за отчетный период:</p>
                    <b-table 
                        :items="remunerationTable" 
                        :fields="remunerationTableFields"
                        small
                        bordered
                        class="mb-0"
                        :tbody-tr-class="remunerationRowClass"
                    >
                        <template #cell(revenue)="row">
                            {{ formatCurrencyNumber(row.item.revenue) }}
                        </template>
                        <template #cell(payouts)="row">
                            {{ formatCurrencyNumber(row.item.payouts) }}
                        </template>
                        <template #cell(base)="row">
                            {{ formatCurrencyNumber(row.item.base) }}
                        </template>
                        <template #cell(rate)="row">
                            <span v-if="row.item.rate !== ''">{{ row.item.rate }}%</span>
                        </template>
                        <template #cell(amount)="row">
                            {{ formatCurrencyNumber(row.item.amount) }}
                        </template>
                    </b-table>
                </div>

                <!-- Раздел 3: Бонусные выплаты (если есть бонусы без order_id) -->
                <div class="mb-4" v-if="bonusAmount > 0">
                    <p class="mb-2">
                        3. За регистрацию в Личном кабинете начисляется приветственное бонусное поощрение в размере 
                        <strong>{{ formatCurrencyNumber(bonusAmount) }} рублей</strong>.
                    </p>
                </div>

                <!-- Раздел 4: Итого -->
                <div class="mb-4">
                    <p class="mb-2">
                        <span v-if="bonusAmount > 0">4. </span><span v-else>3. </span> Сумма агентского вознаграждения составляет <strong>{{ totalAmountText }}</strong> 
                        и выплачивается в порядке предусмотренном агентским договором (публичной офертой).
                    </p>
                </div>

                <!-- Реквизиты получателя -->
                <div class="mt-4">
                    <h6 class="mb-3"><strong>Реквизиты Получателя</strong></h6>
                    <b-row>
                        <b-col cols="12" md="6">
                            <p class="mb-1"><strong>ФИО:</strong> {{ agentCompanyName }}</p>
                            <p class="mb-1" v-if="agentCompanyInn"><strong>ИНН:</strong> {{ agentCompanyInn }}</p>
                        </b-col>
                        <b-col cols="12" md="6">
                            <p class="mb-1" v-if="agentCompanyBankAccount"><strong>р/сч:</strong> {{ agentCompanyBankAccount }}</p>
                            <p class="mb-1" v-if="agentCompanyBankName"><strong>Банк:</strong> {{ agentCompanyBankName }}</p>
                            <p class="mb-1" v-if="agentCompanyCorrespondentAccount"><strong>К/с:</strong> {{ agentCompanyCorrespondentAccount }}</p>
                            <p class="mb-1" v-if="agentCompanyBik"><strong>БИК:</strong> {{ agentCompanyBik }}</p>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </b-card>

        <!-- Кнопки действий -->
        <div class="text-right mt-4">
            <b-button 
                v-if="canDownloadAct"
                variant="outline-primary"
                size="sm"
                class="mr-2"
                @click="downloadAct"
                :disabled="loadingGenerateAct"
            >
                <b-spinner small v-if="loadingGenerateAct" class="mr-1"></b-spinner>
                <i class="fas fa-download mr-1" v-else></i>
                Скачать акт
            </b-button>
            <b-button 
                variant="primary"
                size="sm"
                @click="handleMarkActSent"
                :disabled="loadingAct || !canMarkActSent"
            >
                <b-spinner small v-if="loadingAct" class="mr-1"></b-spinner>
                <i class="fas fa-paper-plane mr-1" v-else></i>
                Оригинал акта отправлен
            </b-button>
            <b-button 
                v-if="canMarkActReceived"
                variant="danger"
                size="sm"
                class="ml-2 text-uppercase"
                @click="handleRejectRequest"
                :disabled="loadingRejectRequest"
            >
                <b-spinner small v-if="loadingRejectRequest" class="mr-1"></b-spinner>
                <i class="fas fa-times-circle mr-1" v-else></i>
                Отклонить заявку
            </b-button>
            <b-button 
                v-if="canMarkActReceived"
                variant="success"
                size="sm"
                class="ml-2"
                @click="handleMarkActReceived"
                :disabled="loadingMarkActReceived"
            >
                <b-spinner small v-if="loadingMarkActReceived" class="mr-1"></b-spinner>
                <i class="fas fa-check-circle mr-1" v-else></i>
                Оригинал акта получен
            </b-button>
            <b-button 
                v-if="canMarkFundsSent"
                variant="success"
                size="sm"
                class="ml-2"
                @click="handleMarkFundsSent"
                :disabled="loadingMarkFundsSent"
            >
                <b-spinner small v-if="loadingMarkFundsSent" class="mr-1"></b-spinner>
                <i class="fas fa-money-bill-wave mr-1" v-else></i>
                Вознаграждение отправлено
            </b-button>
        </div>
    </div>
</template>

<script>
import { API_AGENT_WITHDRAWAL_REQUESTS } from "@/constants";
import { mapGetters } from 'vuex';

export default {
    name: 'AgentWithdrawalRequestView',
    props: {
        withdrawalRequest: {
            type: Object,
            default: null
        },
        availableAmount: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            settlements: [],
            loadingAct: false,
            loadingGenerateAct: false,
            loadingMarkActReceived: false,
            loadingMarkFundsSent: false,
            loadingRejectRequest: false
        }
    },
    computed: {
        ...mapGetters('auth', ['currentUser']),
        isAdminOrAccountant() {
            if (!this.currentUser || !this.currentUser.role) {
                return false;
            }
            const role = this.currentUser.role;
            return role.includes('administrator') || 
                   role.includes('president') || 
                   role.includes('accountant') || 
                   role.includes('head-accountant');
        },
        canMarkActReceived() {
            return this.isAdminOrAccountant && 
                   this.withdrawalRequest && 
                   (this.withdrawalRequest.status === 'act_sent' || 
                    this.withdrawalRequest.status === 'act_generated');
        },
        canMarkFundsSent() {
            return this.isAdminOrAccountant && 
                   this.withdrawalRequest && 
                   this.withdrawalRequest.status === 'act_received';
        },
        settlementFields() {
            return [
                { key: 'id', label: 'ID', sortable: true },
                { key: 'title', label: 'Название', sortable: true },
                { key: 'amount', label: 'Сумма', sortable: true },
                { key: 'status', label: 'Статус', sortable: true },
                { key: 'created_at', label: 'Дата создания', sortable: true }
            ];
        },
        requestedAmount() {
            return this.withdrawalRequest ? parseFloat(this.withdrawalRequest.amount || 0) : 0;
        },
        remainingBalance() {
            if (!this.withdrawalRequest) {
                return this.availableAmount;
            }
            return Math.max(0, this.availableAmount - this.requestedAmount);
        },
        // Данные агентской компании
        agentCompanyName() {
            const request = this.withdrawalRequest || {};
            const company = request.agent_company || {};
            return company.user.name || 'Не указано';
        },
        agentCompanyInn() {
            const request = this.withdrawalRequest || {};
            const company = request.agent_company || {};
            return company.inn || null;
        },
        agentCompanyBankAccount() {
            const request = this.withdrawalRequest || {};
            const company = request.agent_company || {};
            return company.bank_account || null;
        },
        agentCompanyBankName() {
            const request = this.withdrawalRequest || {};
            const company = request.agent_company || {};
            return company.bank_name || null;
        },
        agentCompanyCorrespondentAccount() {
            const request = this.withdrawalRequest || {};
            const company = request.agent_company || {};
            return company.correspondent_account || null;
        },
        agentCompanyBik() {
            const request = this.withdrawalRequest || {};
            const company = request.agent_company || {};
            return company.bik || null;
        },
        // Период отчета
        periodStart() {
            const dates = (this.settlements || [])
                .map(s => this.parseDate(s.created_at))
                .filter(Boolean);
            if (dates.length > 0) {
                return new Date(Math.min(...dates.map(d => d.getTime())));
            }
            const request = this.withdrawalRequest || {};
            return this.parseDate(request.created_at) || new Date();
        },
        periodEnd() {
            const dates = (this.settlements || [])
                .map(s => this.parseDate(s.created_at))
                .filter(Boolean);
            if (dates.length > 0) {
                return new Date(Math.max(...dates.map(d => d.getTime())));
            }
            const request = this.withdrawalRequest || {};
            return this.parseDate(request.created_at) || new Date();
        },
        // Уникальные клиенты
        uniqueClients() {
            if (!this.settlements || this.settlements.length === 0) return [];
            const clientsMap = new Map();
            this.settlements.forEach(settlement => {
                const order = settlement.order || null;
                const company = order && order.company ? order.company : null;
                if (order && company) {
                    const key = order.id || settlement.order_id;
                    if (!clientsMap.has(key)) {
                        const contract = order.contract || null;
                        const contractName = contract && contract.name ? contract.name : null;
                        const contractDate = contract && contract.date_of_document ? contract.date_of_document : null;
                        const contractPath = contract && contract.path ? contract.path : null;
                        clientsMap.set(key, {
                            name: company.name || 'Не указан',
                            inn: company.inn || '',
                            orderId: order.id || settlement.order_id,
                            contractNumber: contractName || order.ordering_number || '',
                            contractDate: contractDate || settlement.created_at || order.created_at || null,
                            contractPath: contractPath
                        });
                    }
                }
            });
            return Array.from(clientsMap.values());
        },
        uniqueClientsCount() {
            return this.uniqueClients.length;
        },
        clientsTable() {
            return this.uniqueClients.map((client, index) => ({
                number: index + 1,
                name: client.name,
                inn: client.inn,
                contract: client.contractNumber
                    ? `${client.contractNumber}${client.contractDate ? ' от ' + this.formatActDate(client.contractDate) : ''}`
                    : 'Не указан'
            }));
        },
        clientsTableFields() {
            return [
                { key: 'number', label: '№ п/п' },
                { key: 'name', label: 'Наименование клиента' },
                { key: 'inn', label: 'ИНН' },
                { key: 'contract', label: 'Номер и дата договора с ООО «ТАНАИС БРОКЕР»' }
            ];
        },
        // Расчет вознаграждения
        remunerationTable() {
            const ordersMap = new Map();
            (this.settlements || []).forEach(settlement => {
                const orderId = settlement.order_id;
                if (!orderId || ordersMap.has(orderId)) {
                    return;
                }

                const financeSummary = settlement.finance_summary || {};
                const revenue = parseFloat(financeSummary.incoming_without_nds || 0);
                const payouts = parseFloat(financeSummary.outgoing_without_nds || 0);
                const rate = settlement.commission_rate ? parseFloat(settlement.commission_rate) : 0;
                const base = revenue - payouts;
                let amount = 0;

                if (rate) {
                    amount = parseFloat((base * rate / 100).toFixed(2));
                } else if (settlement.amount) {
                    amount = parseFloat(settlement.amount);
                }

                ordersMap.set(orderId, {
                    orderId,
                    revenue,
                    payouts,
                    base,
                    rate,
                    amount
                });
            });

            const rows = Array.from(ordersMap.values());

            if (rows.length > 0) {
                const totals = rows.reduce((acc, row) => {
                    acc.revenue += row.revenue;
                    acc.payouts += row.payouts;
                    acc.base += row.base;
                    acc.amount += row.amount;
                    return acc;
                }, { revenue: 0, payouts: 0, base: 0, amount: 0 });

                rows.push({
                    isTotal: true,
                    revenue: totals.revenue,
                    payouts: totals.payouts,
                    base: totals.base,
                    rate: '',
                    amount: totals.amount
                });
            }

            return rows;
        },
        remunerationTableFields() {
            return [
                { key: 'revenue', label: 'Выручка ООО «ТАНАИС БРОКЕР» за оказанные услуги (руб, без НДС)' },
                { key: 'payouts', label: 'Выплаты ООО «ТАНАИС БРОКЕР» третьим лицам (руб, без НДС)' },
                { key: 'base', label: 'База расчета вознаграждения (руб, без НДС)' },
                { key: 'rate', label: 'Ставка вознаграждения, %' },
                { key: 'amount', label: 'Сумма вознаграждения (руб, без НДС)' }
            ];
        },
        // Бонусы без order_id
        bonusAmount() {
            if (!this.settlements) return 0;
            return this.settlements
                .filter(s => !s.order_id && parseFloat(s.amount || 0) > 0)
                .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);
        },
        // Итого
        totalRemuneration() {
            const rows = this.remunerationTable.filter(row => !row.isTotal);
            const remuneration = rows.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);
            return remuneration;
        },
        totalAmount() {
            return this.totalRemuneration + this.bonusAmount;
        },
        totalAmountText() {
            const amount = this.totalAmount;
            const rubles = Math.floor(amount);
            const kopecks = Math.round((amount - rubles) * 100);
            const rublesText = this.capitalize(this.numberToWords(rubles));
            const rubleWord = this.declension(rubles, ['рубль', 'рубля', 'рублей']);
            const kopeckWord = this.declension(kopecks, ['копейка', 'копейки', 'копеек']);
            const amountFormatted = this.formatCurrencyNumber(amount);
            const kopecksFormatted = String(kopecks).padStart(2, '0');
            return `${amountFormatted} (${rublesText} ${rubleWord}) ${kopecksFormatted} ${kopeckWord}.`;
        },
        canMarkActSent() {
            return this.withdrawalRequest && 
                   this.withdrawalRequest.status === 'act_generated';
        },
        canDownloadAct() {
            if (!this.withdrawalRequest) return false;
            // Разрешаем скачивание/генерацию акта для всех статусов, кроме отмененных
            // Метод downloadAct сам определит, нужно ли генерировать акт или скачать существующий
            const blockedStatuses = ['cancelled'];
            return !blockedStatuses.includes(this.withdrawalRequest.status);
        }
    },
    watch: {
        withdrawalRequest: {
            handler(newVal) {
                if (!newVal) {
                    this.settlements = [];
                    return;
                }

                if (Array.isArray(newVal.settlements_details)) {
                    const prepared = newVal.settlements_details.map(item => ({ ...item }));
                    prepared.sort((a, b) => {
                        const dateA = this.parseDate(a.created_at);
                        const dateB = this.parseDate(b.created_at);
                        if (!dateA && !dateB) return 0;
                        if (!dateA) return 1;
                        if (!dateB) return -1;
                        return dateA.getTime() - dateB.getTime();
                    });
                    this.settlements = prepared;
                } else {
                    this.settlements = [];
                }
            },
            immediate: true
        }
    },
    methods: {
        parseDate(value) {
            if (!value) return null;
            if (value instanceof Date) return value;
            if (typeof value === 'number') {
                const date = new Date(value);
                return isNaN(date.getTime()) ? null : date;
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (!trimmed) return null;

                if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
                    const normalized = trimmed.replace(' ', 'T');
                    const date = new Date(normalized);
                    return isNaN(date.getTime()) ? null : date;
                }

                const match = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
                if (match) {
                    const [, day, month, year, hours = '00', minutes = '00', seconds = '00'] = match;
                    const date = new Date(
                        parseInt(year, 10),
                        parseInt(month, 10) - 1,
                        parseInt(day, 10),
                        parseInt(hours, 10),
                        parseInt(minutes, 10),
                        parseInt(seconds, 10)
                    );
                    return isNaN(date.getTime()) ? null : date;
                }
            }

            return null;
        },
        declension(number, forms) {
            const absNumber = Math.abs(number) % 100;
            const lastDigit = absNumber % 10;

            if (absNumber > 10 && absNumber < 20) {
                return forms[2];
            }
            if (lastDigit > 1 && lastDigit < 5) {
                return forms[1];
            }
            if (lastDigit === 1) {
                return forms[0];
            }
            return forms[2];
        },
        capitalize(value) {
            if (!value) return value;
            return value.charAt(0).toUpperCase() + value.slice(1);
        },
        formatCurrency(value) {
            try {
                return new Intl.NumberFormat('ru-RU', { 
                    style: 'currency', 
                    currency: 'RUB', 
                    maximumFractionDigits: 0 
                }).format(Number(value || 0));
            } catch (e) {
                return `${value} ₽`;
            }
        },
        formatCurrencyNumber(value) {
            try {
                return new Intl.NumberFormat('ru-RU', { 
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(Number(value || 0));
            } catch (e) {
                return `${value || 0}`;
            }
        },
        formatDate(dateString) {
            const date = this.parseDate(dateString);
            if (!date) return '-';
            return date.toLocaleDateString('ru-RU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        formatActDate(dateInput) {
            const date = this.parseDate(dateInput);
            if (!date) return '';
            const day = String(date.getDate()).padStart(2, '0');
            const monthNames = [
                'января',
                'февраля',
                'марта',
                'апреля',
                'мая',
                'июня',
                'июля',
                'августа',
                'сентября',
                'октября',
                'ноября',
                'декабря'
            ];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            return `«${day}» ${month} ${year}`;
        },
        numberToWordsChunk(num, units, teens, tens, hundreds, gender = 0) {
            const hundred = Math.floor(num / 100);
            const ten = Math.floor((num % 100) / 10);
            const unit = num % 10;

            const parts = [];

            if (hundred > 0) {
                parts.push(hundreds[hundred]);
            }

            if (ten === 1) {
                parts.push(teens[unit]);
            } else {
                if (ten > 1) {
                    parts.push(tens[ten]);
                }
                if (unit > 0) {
                    parts.push(units[gender][unit]);
                }
            }

            return parts.join(' ').trim();
        },
        numberToWords(num) {
            if (num === 0) return 'ноль';

            const units = [
                ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'],
                ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять']
            ];
            const teens = ['десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать'];
            const tens = ['', 'десять', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
            const hundreds = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];
            const scales = [
                ['', '', ''],
                ['тысяча', 'тысячи', 'тысяч'],
                ['миллион', 'миллиона', 'миллионов'],
                ['миллиард', 'миллиарда', 'миллиардов']
            ];

            const parts = [];
            let remainder = num;
            let scaleIndex = 0;

            while (remainder > 0 && scaleIndex < scales.length) {
                const chunk = remainder % 1000;
                if (chunk > 0) {
                    const gender = scaleIndex === 1 ? 1 : 0;
                    const chunkWords = this.numberToWordsChunk(chunk, units, teens, tens, hundreds, gender);
                    const scaleWords = scales[scaleIndex];
                    const scaleWord = scaleWords.length ? this.declension(chunk, scaleWords) : '';
                    const segment = [chunkWords, scaleWord].filter(Boolean).join(' ').trim();
                    if (segment) {
                        parts.unshift(segment);
                    }
                }
                remainder = Math.floor(remainder / 1000);
                scaleIndex += 1;
            }

            return parts.join(' ').trim();
        },
        remunerationRowClass(item) {
            if (item && item.isTotal) {
                return 'font-weight-bold bg-light';
            }
            return '';
        },
        async handleMarkActSent() {
            if (!this.withdrawalRequest || !this.withdrawalRequest.id) {
                this.$bvToast.toast('Заявка не найдена', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            
            this.loadingAct = true;
            try {
                const response = await api.call('post', `${API_AGENT_WITHDRAWAL_REQUESTS}/${this.withdrawalRequest.id}/mark-act-sent`);
                
                // Определяем данные заявки из ответа
                const updatedRequest = (response.data && response.data.data) 
                    ? response.data.data 
                    : (response.data && response.data.id) 
                        ? response.data 
                        : null;
                
                if (updatedRequest) {
                    this.$emit('request-updated', updatedRequest);
                    this.$bvToast.toast('Акт отмечен как отправленный. По получению акта с нашей стороны, вы будете проинформированы.', {
                        title: 'Успешно',
                        variant: 'success',
                        solid: true
                    });
                } else {
                    console.error('Неожиданная структура ответа:', response);
                    throw new Error('Неожиданная структура ответа от сервера');
                }
            } catch (error) {
                console.error('Ошибка при отправке акта:', error);
                let errorMessage = 'Ошибка при обновлении статуса';
                
                if (error.response) {
                    if (error.response.data) {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.response.data.error) {
                            if (typeof error.response.data.error === 'string') {
                                errorMessage = error.response.data.error;
                            } else if (error.response.data.error.message) {
                                errorMessage = error.response.data.error.message;
                            }
                        }
                    }
                    if (error.response.status === 403) {
                        errorMessage = 'У вас нет прав для выполнения этого действия';
                    } else if (error.response.status === 400) {
                        errorMessage = error.response.data && error.response.data.message 
                            ? error.response.data.message 
                            : 'Некорректный запрос';
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loadingAct = false;
            }
        },
        async downloadAct() {
            if (!this.withdrawalRequest || !this.withdrawalRequest.id) {
                this.$bvToast.toast('Заявка не найдена', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            // Форматируем дату операции в формат ДДММГОД
            const formatDateForFileName = (dateString) => {
                if (!dateString) return '';
                const date = this.parseDate(dateString);
                if (!date) return '';
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}${month}${year}`;
            };

            // Используем дату создания заявки или дату генерации акта
            const operationDate = this.withdrawalRequest.act_generated_at || this.withdrawalRequest.created_at;
            const dateStr = formatDateForFileName(operationDate);
            let fileName = dateStr ? `Акт_агента_${dateStr}.docx` : `Акт_агента_${this.withdrawalRequest.id}.docx`;

            // Если акт уже сформирован, пытаемся скачать существующий документ
            if (this.withdrawalRequest.act_generated_at && this.withdrawalRequest.documents) {
                const documents = Array.isArray(this.withdrawalRequest.documents) 
                    ? this.withdrawalRequest.documents 
                    : [];
                const actDoc = documents.find(doc => doc.path);
                
                if (actDoc && actDoc.path) {
                    const fullUrl = actDoc.path.startsWith('http') 
                        ? actDoc.path 
                        : window.location.origin + (actDoc.path.startsWith('/') ? '' : '/') + actDoc.path;
                    
                    const link = document.createElement('a');
                    link.href = fullUrl;
                    link.download = fileName;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    return;
                }
            }

            // Если акт еще не сформирован, генерируем его
            this.loadingGenerateAct = true;
            try {
                const response = await api.call('post', `${API_AGENT_WITHDRAWAL_REQUESTS}/${this.withdrawalRequest.id}/generate-act`);
                const updatedRequest = response.data && response.data.data ? response.data.data : null;
                const link = response.data && response.data.link ? response.data.link : null;

                if (updatedRequest) {
                    this.$emit('request-updated', updatedRequest);
                    // Обновляем дату после генерации акта
                    const updatedDate = updatedRequest.act_generated_at || updatedRequest.created_at;
                    const updatedDateStr = formatDateForFileName(updatedDate);
                    if (updatedDateStr) {
                        fileName = `Акт_агента_${updatedDateStr}.docx`;
                    }
                }

                if (link) {
                    const downloadLink = document.createElement('a');
                    downloadLink.href = link.startsWith('http') 
                        ? link 
                        : window.location.origin + (link.startsWith('/') ? '' : '/') + link;
                    downloadLink.download = fileName;
                    downloadLink.target = '_blank';
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                }

                this.$bvToast.toast('Акт сформирован и подготовлен к скачиванию', {
                    title: 'Успешно',
                    variant: 'success',
                    solid: true
                });
            } catch (error) {
                console.error('Ошибка генерации акта:', error);
                let errorMessage = 'Ошибка при формировании акта';
                if (error.response && error.response.data) {
                    if (error.response.data.message) {
                        errorMessage = error.response.data.message;
                    } else if (error.response.data.error) {
                        errorMessage = typeof error.response.data.error === 'string'
                            ? error.response.data.error
                            : error.response.data.error.message || errorMessage;
                    }
                }
                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loadingGenerateAct = false;
            }
        },
        getStatusVariant(status) {
            const variants = {
                'pending': 'warning',
                'approved': 'success',
                'completed': 'primary',
                'rejected': 'danger'
            };
            return variants[status] || 'secondary';
        },
        getStatusLabel(status) {
            const labels = {
                'pending': 'Ожидает одобрения',
                'approved': 'Одобрено',
                'completed': 'Выполнено',
                'rejected': 'Отклонено'
            };
            return labels[status] || status;
        },
        getWithdrawalStatusVariant(status) {
            const variants = {
                'pending': 'warning',
                'act_generated': 'info',
                'act_sent': 'primary',
                'act_received': 'success',
                'funds_sent': 'success',
                'cancelled': 'danger'
            };
            return variants[status] || 'secondary';
        },
        getWithdrawalStatusLabel(status) {
            const labels = {
                'pending': 'Заявка создана',
                'act_generated': 'Акт сформирован',
                'act_sent': 'Акт отправлен по почте',
                'act_received': 'Получили оригинал акта',
                'funds_sent': 'Средства отправлены',
                'cancelled': 'Отменена'
            };
            return labels[status] || status;
        },
        async handleMarkActReceived() {
            if (!this.withdrawalRequest || !this.withdrawalRequest.id) {
                this.$bvToast.toast('Заявка не найдена', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            
            this.loadingMarkActReceived = true;
            try {
                const response = await api.call('post', `${API_AGENT_WITHDRAWAL_REQUESTS}/${this.withdrawalRequest.id}/mark-act-received`);
                
                // Определяем данные заявки из ответа
                const updatedRequest = (response.data && response.data.data) 
                    ? response.data.data 
                    : (response.data && response.data.id) 
                        ? response.data 
                        : null;
                
                if (updatedRequest) {
                    this.$emit('request-updated', updatedRequest);
                    this.$bvToast.toast('Акт отмечен как полученный', {
                        title: 'Успешно',
                        variant: 'success',
                        solid: true
                    });
                } else {
                    console.error('Неожиданная структура ответа:', response);
                    throw new Error('Неожиданная структура ответа от сервера');
                }
            } catch (error) {
                console.error('Ошибка при отметке акта как полученного:', error);
                let errorMessage = 'Ошибка при обновлении статуса';
                
                if (error.response) {
                    if (error.response.data) {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.response.data.error) {
                            if (typeof error.response.data.error === 'string') {
                                errorMessage = error.response.data.error;
                            } else if (error.response.data.error.message) {
                                errorMessage = error.response.data.error.message;
                            }
                        }
                    }
                    if (error.response.status === 403) {
                        errorMessage = 'У вас нет прав для выполнения этого действия';
                    } else if (error.response.status === 400) {
                        errorMessage = error.response.data && error.response.data.message 
                            ? error.response.data.message 
                            : 'Некорректный запрос';
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loadingMarkActReceived = false;
            }
        },
        async handleMarkFundsSent() {
            if (!this.withdrawalRequest || !this.withdrawalRequest.id) {
                this.$bvToast.toast('Заявка не найдена', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }
            
            this.loadingMarkFundsSent = true;
            try {
                const response = await api.call('post', `${API_AGENT_WITHDRAWAL_REQUESTS}/${this.withdrawalRequest.id}/mark-funds-sent`);
                
                // Определяем данные заявки из ответа
                const updatedRequest = (response.data && response.data.data) 
                    ? response.data.data 
                    : (response.data && response.data.id) 
                        ? response.data 
                        : null;
                
                if (updatedRequest) {
                    this.$emit('request-updated', updatedRequest);
                    this.$bvToast.toast('Средства отмечены как отправленные', {
                        title: 'Успешно',
                        variant: 'success',
                        solid: true
                    });
                } else {
                    console.error('Неожиданная структура ответа:', response);
                    throw new Error('Неожиданная структура ответа от сервера');
                }
            } catch (error) {
                console.error('Ошибка при отметке средств как отправленных:', error);
                let errorMessage = 'Ошибка при обновлении статуса';
                
                if (error.response) {
                    if (error.response.data) {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.response.data.error) {
                            if (typeof error.response.data.error === 'string') {
                                errorMessage = error.response.data.error;
                            } else if (error.response.data.error.message) {
                                errorMessage = error.response.data.error.message;
                            }
                        }
                    }
                    if (error.response.status === 403) {
                        errorMessage = 'У вас нет прав для выполнения этого действия';
                    } else if (error.response.status === 400) {
                        errorMessage = error.response.data && error.response.data.message 
                            ? error.response.data.message 
                            : 'Некорректный запрос';
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loadingMarkFundsSent = false;
            }
        },
        async handleRejectRequest() {
            if (!this.withdrawalRequest || !this.withdrawalRequest.id) {
                this.$bvToast.toast('Заявка не найдена', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            // Подтверждение отклонения заявки
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Вы уверены, что хотите отклонить эту заявку на вывод средств?',
                {
                    title: 'Подтверждение отклонения',
                    okVariant: 'danger',
                    okTitle: 'Отклонить',
                    cancelTitle: 'Отмена',
                    centered: true
                }
            );

            if (!confirmed) {
                return;
            }
            
            this.loadingRejectRequest = true;
            try {
                const response = await api.call('post', `${API_AGENT_WITHDRAWAL_REQUESTS}/${this.withdrawalRequest.id}/reject`);
                
                // Определяем данные заявки из ответа
                const updatedRequest = (response.data && response.data.data) 
                    ? response.data.data 
                    : (response.data && response.data.id) 
                        ? response.data 
                        : null;
                
                if (updatedRequest) {
                    this.$emit('request-updated', updatedRequest);
                    this.$bvToast.toast('Заявка отклонена', {
                        title: 'Успешно',
                        variant: 'success',
                        solid: true
                    });
                } else {
                    console.error('Неожиданная структура ответа:', response);
                    throw new Error('Неожиданная структура ответа от сервера');
                }
            } catch (error) {
                console.error('Ошибка при отклонении заявки:', error);
                let errorMessage = 'Ошибка при отклонении заявки';
                
                if (error.response) {
                    if (error.response.data) {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.response.data.error) {
                            if (typeof error.response.data.error === 'string') {
                                errorMessage = error.response.data.error;
                            } else if (error.response.data.error.message) {
                                errorMessage = error.response.data.error.message;
                            }
                        }
                    }
                    if (error.response.status === 403) {
                        errorMessage = 'У вас нет прав для выполнения этого действия';
                    } else if (error.response.status === 400) {
                        errorMessage = error.response.data && error.response.data.message 
                            ? error.response.data.message 
                            : 'Некорректный запрос';
                    }
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                this.$bvToast.toast(errorMessage, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loadingRejectRequest = false;
            }
        }
    }
}
</script>

<style scoped>
/* Стили для компонента просмотра заявки */
::v-deep .table th {
    color: #000;
}
</style>

